/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FormEvent, Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { UserAtom } from "../../../atoms/UserAtom";
import { IWordpressIntegration, WordpressArgs } from "../../../generated/Api";
import { SettingsTab } from "../../../pages/settings";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { AllIntegrationsAtom } from "./Integrations";
import StyledSwitch from "../../Switch";
import { useModal } from "../../Modal/Modal";
import { HelpVideo } from "../../HelpVideo";
import { INTERNAL_UIDS_TO_SEE_CRM } from "../../../utils/constants";
import { localStorageGetItem } from "../../../utils/LocalStorage";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }
`;

const TitleWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  margin-bottom: 24px;

  > img {
    width: 67px;
    height: 67px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
    border-bottom: unset;
    padding-bottom: unset;
    margin-bottom: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 24px;

  margin-bottom: 24px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 32px;

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;
//!

const FooterColumn = styled.div`
  /* border-top: 1px solid #eaeaeabf; */
  padding-top: 24px;
  margin-top: 24px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

interface WordpressProps {
  handleSelected: (vle: SettingsTab) => void;
}

export default function Wordpress({
  handleSelected,
}: WordpressProps): JSX.Element | null {
  const user = useRecoilValue(UserAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [integrations, setIntegrations] = useRecoilState(AllIntegrationsAtom);
  const modalHandler = useModal();
  const wordpressIntegration =
    (integrations.find(
      (i) => i?.type === "WORDPRESS"
    ) as IWordpressIntegration) || undefined;

  const [args, setArgs] = useState<WordpressArgs | undefined>(
    wordpressIntegration?.args
  );

  const [showBetaFeature, setShowBetaFeature] = useState(false);

  useEffect(() => {
    let internalUid = localStorageGetItem("INTERNAL_USER_UID") as string;

    if (INTERNAL_UIDS_TO_SEE_CRM.includes(internalUid))
      setShowBetaFeature(true);

    // I don't have an internal user :(
    if (user && user.email === "nicole@wordswithgrim.com")
      setShowBetaFeature(true);
  }, [user]);

  async function updatePostByCategory(value: boolean) {
    if (!user || !wordpressIntegration) return;
    await IntegrationAPI.update({
      userUid: user.userUid,
      integrationId: wordpressIntegration._id,
      integration: {
        args: {
          ...wordpressIntegration?.args,
          postByCategory: value,
        },
      },
    });

    setArgs({
      ...wordpressIntegration?.args,
      postByCategory: value,
    });

    getIntegrations();
  }

  async function getIntegrations() {
    let integrations = await IntegrationAPI.getAll();
    setIntegrations(integrations);
  }

  async function handleDisconnect(e: FormEvent) {
    e.preventDefault();

    if (!user) return;
    if (!wordpressIntegration) return;

    try {
      setIsLoading(true);
      const isDisconnect = await IntegrationAPI.disconnect(
        wordpressIntegration._id
      );

      if (isDisconnect) {
        await getIntegrations();

        setTimeout(() => {
          handleSelected(SettingsTab.INTEGRATIONS);
        }, 100);

        return;
      }

      toast("Error while disconnecting, please try again later", {
        type: "error",
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!user) return;

    if (!args || !args.applicationPassword || !args.email || !args.hostname) {
      toast("Please fill in the fields correctly", {
        type: "error",
      });

      return;
    }

    try {
      setIsLoading(true);

      const parsedUrl = new URL(args.hostname).origin;

      const isValid = await IntegrationAPI.checkWordpress({
        userUid: user.userUid,
        args: {
          ...args,
          hostname: parsedUrl,
          shouldPostAsDraft: !!args.shouldPostAsDraft,
        },
        type: "WORDPRESS",
      });

      if (!isValid) {
        toast(
          "Error on verifying the Wordpress application. Check it and try again.",
          {
            type: "error",
          }
        );
        return;
      }

      const isCreated = await IntegrationAPI.create({
        integration: {
          userUid: user.userUid,
          args: {
            ...args,
            hostname: parsedUrl,
            shouldPostAsDraft: !!args.shouldPostAsDraft,
          },
          type: "WORDPRESS",
        },
      });

      if (isCreated) {
        await getIntegrations();

        setTimeout(() => {
          handleSelected(SettingsTab.INTEGRATIONS);
        }, 100);

        return;
      }

      toast(
        "Error on verifying the Wordpress application. Check it and try again.",
        {
          type: "error",
        }
      );
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }

    return;
  }

  function showHelpVideo() {
    modalHandler.pushModal(
      <HelpVideo defaultVideoUrl="https://player.vimeo.com/video/836724068/" />,
      "Generate a Wordpress Application Password"
    );
  }

  return (
    <Container>
      <TitleWrapper>
        <TextWrapper>
          <MainTitle>Wordpress Integration</MainTitle>
          <MainText>
            Please provide the following information about the application in
            the area below. These details will be used for authentication.
          </MainText>
        </TextWrapper>

        <img src="./images/wordpress.png" alt="wordpress" />
      </TitleWrapper>

      <TextWrapper>
        <MainTitle>Information</MainTitle>
        <MainText>
          You can locate your Wordpress information inside your Wordpress Admin.
        </MainText>
      </TextWrapper>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Email</SecondaryTitle>
            <MainText>Your user email.</MainText>
          </div>

          <StyledInput
            css={css`
              max-width: 237px;
            `}
            disabled={!!wordpressIntegration}
            defaultValue={wordpressIntegration?.args?.email}
            onChange={(e) => setArgs({ ...args, email: e.target.value })}
            placeholder="Put your user email here"
          />
        </SpaceBetween>
      </FormContent>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Application password</SecondaryTitle>
            <MainText>Your application password.</MainText>
          </div>

          <StyledInput
            css={css`
              max-width: 237px;
            `}
            disabled={!!wordpressIntegration}
            defaultValue={wordpressIntegration?.args?.applicationPassword}
            onChange={(e) =>
              setArgs({ ...args, applicationPassword: e.target.value })
            }
            placeholder="Put your application password here"
          />
        </SpaceBetween>
      </FormContent>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Wordpress URL</SecondaryTitle>
            <MainText>Your application Wordpress URL.</MainText>
          </div>

          <StyledInput
            css={css`
              max-width: 237px;
            `}
            disabled={!!wordpressIntegration}
            defaultValue={wordpressIntegration?.args?.hostname}
            onChange={(e) => setArgs({ ...args, hostname: e.target.value })}
            placeholder="Put your Wordpress URL here"
          />
        </SpaceBetween>
      </FormContent>

      <FormContent>
        <SpaceBetween>
          <div className="flex-column-wrapper">
            <SecondaryTitle>Should post as draft</SecondaryTitle>
          </div>

          <StyledSwitch
            toggled={
              args?.shouldPostAsDraft ||
              !!wordpressIntegration?.args?.shouldPostAsDraft
            }
            disabled={!!wordpressIntegration}
            whenToggled={(t) =>
              setArgs({
                ...args,
                shouldPostAsDraft: t,
              })
            }
          />
          {/* <StyledInput
            css={css`
              max-width: 237px;
            `}
            disabled={!!wordpressIntegration}
            defaultValue={wordpressIntegration?.args?.hostname}
            onChange={(e) => setArgs({ ...args, hostname: e.target.value })}
            placeholder="Put your Wordpress URL here"
          /> */}
        </SpaceBetween>
      </FormContent>

      {showBetaFeature && (
        <FormContent>
          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Post by category</SecondaryTitle>
            </div>

            <StyledSwitch
              toggled={
                args?.postByCategory ||
                !!wordpressIntegration?.args?.postByCategory
              }
              disabled={!wordpressIntegration}
              whenToggled={(t) => updatePostByCategory(t)}
            />
          </SpaceBetween>
          {(args?.postByCategory ||
            !!wordpressIntegration?.args?.postByCategory) && (
            <StyledButton
              inverted
              css={css`
                height: 45px;
              `}
              onClick={() => handleSelected(SettingsTab.WORDPRESSCATEGORIES)}
            >
              Manage Categories
            </StyledButton>
          )}
        </FormContent>
      )}
      <TextWrapper>
        <MainTitle>Application Password set up</MainTitle>
        <MainText>
          Goto your <b>WordPress Admin</b>, <b>{`Users -> Profile`}</b>, scroll
          down to <b>Application Passwords</b> and create a new application
          password with the name <b>berserkermail-integration</b>. Remember to
          hit save at the bottom of that screen, then simply copy and paste this
          password onto the integration section of your BerserkerMail account.
          <br />
          <br />
          If you have any questions please contact us at help@berserkermail.com.
          <br />
          <br />
          <u
            css={css`
              cursor: pointer;
            `}
            onClick={showHelpVideo}
          >
            <b>Please watch this video for a detailed explanation</b>
          </u>
        </MainText>
      </TextWrapper>

      <FooterColumn>
        {wordpressIntegration ? (
          <StyledButton
            css={css`
              height: 45px;
              width: 120px;
            `}
            isLoading={isLoading}
            onClick={handleDisconnect}
          >
            Disconnect
          </StyledButton>
        ) : (
          <StyledButton
            css={css`
              height: 45px;
              width: 210px;
            `}
            isLoading={isLoading}
            onClick={handleSubmit}
            disable={!args}
          >
            Connect with Wordpress
          </StyledButton>
        )}
      </FooterColumn>
    </Container>
  );
}
